import React, { memo, useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { withNamespaces } from "react-i18next";
import { Row, Col, Card, CardBody, Container } from "reactstrap";
import RequestTab from "./request";
import MessageComponent from "./message";
import LiaTab from "./lia";
import TaskManagment from "./taskManagment";
import TaskDetails from "src/modules/data-protection/components/RisksAndTasks/tasks/details";
import Tabs from "./tabs";
import { useQuery } from "@tanstack/react-query";
import { useParams, useHistory, useLocation } from "react-router-dom";
import ReportIncidenService from "../components/api/ReportIncidenService";
import Navigator from "src/components/Navigator";
import OverViewHeader from "../components/overview-header";
import { toast } from "react-toastify";
import { getMailboxBackLocationFromLocation } from "src/modules/mailer/helpers";

const TAB_BREADCRUMB_TITLES = {
  request: "Request",
  lia: "LIA",
  messages: "Messages",
  "internal-comments": "Internal Comments",
  "task-management": "Task Management",
};

const SubjectDetails = (props) => {
  const { t } = props;
  const [taskDetailPage, setTaskDetailPage] = useState(null);
  const [analystAdminsList, setAnalystAdminsList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [messagesPrefillHtml, setMessagesPrefillHtml] = useState(null);
  const { caseId } = useParams();
  const history = useHistory();
  const location = useLocation()

  const handleFetchCategories = useQuery({
    queryKey: ["report-subject-fetch-categories"],
    queryFn: async () => {
      const service = ReportIncidenService.getInstance();

      return await service.FetchCategoriesRequest();
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast(props.t("An error occurred while fetching owners."), {
        type: "error",
      });
    },
  });

  const {
    data: report,
    isFetching: reportIsFetching,
    refetch: refetchReportDetails
  } = useQuery({
    queryKey: ["data-protection-data-subject-report-details", caseId],
    queryFn: async () => {
      const service = ReportIncidenService.getInstance();
      return await service.FetchIncidentReportOverView(caseId);
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast(t("An error occurred while fetching overview content."), {
        type: "error",
      });
    },
  });

  const handleFetchAnalystAndAdminAnalystList = useQuery({
    queryKey: ["fetch-analyst-and-admin-analyst-list"],
    queryFn: async () => {
      const service = ReportIncidenService.getInstance();

      return await service.FetchAnalystsAndAnalystAdmins();
    },
    cacheTime: 0,
    refetchOnWindowFocus: false,
    onError: (error) => {
      toast(t("An error occurred while fetching analyst admins list."), {
        type: "error",
      });
    },
  });

  useEffect(() => {
    if (handleFetchCategories.data) {
      const { data } = handleFetchCategories;
      const translatedData = data.map(d => ({
          ...d,
          title: t(d?.title)
        })
      );
      setCategories(translatedData);
    }
  }, [handleFetchCategories.data, t]);

  useEffect(() => {
    if (
      handleFetchAnalystAndAdminAnalystList.data &&
      Array.isArray(handleFetchAnalystAndAdminAnalystList.data)
    ) {
      const newList = handleFetchAnalystAndAdminAnalystList.data.map(
        (analyst) => {
          return {
            value: analyst.id,
            label: `${analyst.first_name} ${analyst.last_name}`,
          };
        }
      );

      setAnalystAdminsList(newList);
    }
  }, [handleFetchAnalystAndAdminAnalystList.data]);

  const handleMessagesPrefillConsumed = useCallback(() => {
    setMessagesPrefillHtml(null);
  }, []);

  const handleCopyToMessages = useCallback(
    (html) => {
      setMessagesPrefillHtml(html);
      history.push({
        pathname: `/admin/data-protection/data-subject-request/details/${caseId}`,
        search: '?tab=messages',
        state: location.state,
      });
    },
    [caseId, history, location.state]
  );


  const renderSelectedTabContent = () => {
    const searchParams = new URLSearchParams(location.search);

    if (
      handleFetchCategories.isLoading ||
      handleFetchCategories.isFetching ||
      reportIsFetching ||
      !caseId
    ) {
      return (
        <div
          className="dt-field dt-skeleton"
          style={{ width: "100%", height: "320px" }}
        ></div>
      );
    }

    switch (searchParams.get("tab")) {
      case "request":
        return (
          <React.Fragment>
            <RequestTab
              categories={categories}
              caseId={caseId}
              report={report}
              reportRefetch={refetchReportDetails}
              reportLoading={reportIsFetching}
              analystAdminsList={analystAdminsList}
              adminsListIsLoading={
                handleFetchAnalystAndAdminAnalystList.isFetching ||
                handleFetchAnalystAndAdminAnalystList.isLoading
              }
              
            />
          </React.Fragment>
        );
      case "messages":
        return (
          <React.Fragment>
            <MessageComponent 
            type="message" 
            caseId={caseId} 
            reportDetails={report}
            categories={categories}
            prefillHtml={messagesPrefillHtml}
            onPrefillConsumed={handleMessagesPrefillConsumed}
            />
          </React.Fragment>
        );
      case "internal-comments":
        return (
          <React.Fragment>
            <MessageComponent 
              type="internal" 
              caseId={caseId} 
              reportDetails={report} 
              categories={categories}
            />
          </React.Fragment>
        );
      case "lia":
        return (
          <React.Fragment>
            <LiaTab
              caseId={caseId}
              report={report}
              reportRefetch={refetchReportDetails}
              onCopyToMessages={handleCopyToMessages}
            />
          </React.Fragment>
        );
      case "task-management":
        return (
          <React.Fragment>
            {taskDetailPage ? (
              <TaskDetails
                {...taskDetailPage?.data}
                onBackClicked={() => {
                  history.push({
                    pathname: `/admin/data-protection/data-subject-request/details/${caseId}`,
                    search: '?tab=task-management',
                    state: location.state,
                  });
                  setTaskDetailPage(null);
                }}
                onRelatedTaskClicked={(id) => {
                    setTaskDetailPage({
                        type: "task",
                        data: {
                            taskId: id
                        },
                    })
                }}
                ismsControlsOptions={{
                    incident_id: report.id
                }}
              />
            ) : (
              <TaskManagment
                caseId={caseId}
                onTaskSelected={(selectedTaskData) =>
                  setTaskDetailPage({
                    type: "task",
                    data: selectedTaskData,
                  })
                }
                reportId={report.id}
              />
            )}
          </React.Fragment>
        );
      default:
        return (
          <React.Fragment>
            <RequestTab categories={categories} caseId={caseId} />
          </React.Fragment>
        );
    }
  };

  return (
    <div className="page-content ">
      <Container fluid>
        <Row>
          <Col lg="12" className="mb-4">
            <Navigator
              pageTitle={report?.secondary_case_id || "Detail"}
              backButtonOptions={(d) => ({
                ...d,
                enable: true,
                onClick: () => {
                  const mailboxBackLocation = getMailboxBackLocationFromLocation(location);

                  history.push(
                    mailboxBackLocation || "/admin/data-protection/data-subject-request"
                  );
                },
              })}
              breadCrumbs={(() => {
                const searchParams = new URLSearchParams(location.search);
                const activeTab = searchParams.get("tab") || "request";
                const activeTabTitle =
                  TAB_BREADCRUMB_TITLES[activeTab] || "Request";

                return [
                  { title: "SIP", link: "/admin/data-protection/dashboard" },
                  {
                    title: "Data Subject Request",
                    link: `/admin/data-protection/data-subject-request`,
                  },
                  {
                    title: activeTabTitle,
                    link: "#",
                  },
                ];
              })()}
            />
          </Col>

          <Col lg="12" className="mb-4">
            <div className="details-content-wrapper">
              <>
                <Tabs t={t} onTabChange={() => setTaskDetailPage(null)} report={report} />
                <React.Fragment>
                  <Row>
                    <Col xl="12">
                      <Card style={{marginBottom:'0px'}}>
                        <CardBody className="tabOverview mt-2">
                          <div className="d-flex flex-column gap-4 mb-4 mt-1">
                            {taskDetailPage ? null : (
                              <OverViewHeader
                                categories={categories}
                                caseId={caseId}
                                report={report}
                                loading={ reportIsFetching }
                                analystAdminsList={analystAdminsList}
                                adminsListIsLoading={
                                  handleFetchAnalystAndAdminAnalystList.isFetching ||
                                  handleFetchAnalystAndAdminAnalystList.isLoading
                                }
                                reportRefetch={refetchReportDetails}
                              />
                            )}
                            {renderSelectedTabContent()}
                          </div>
                        </CardBody>
                      </Card>
                    </Col>
                  </Row>
                </React.Fragment>
              </>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

const mapStatetoProps = (state) => {
  const { token } = state.Login;
  const { Organization } = state;
  return {
    user: state.Login.user,
    token,
    organization: Organization,
  };
};

export default withNamespaces()(connect(mapStatetoProps)(memo(SubjectDetails)));
